import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { SearchField, Spinner } from '../../components/ui';

// 历史来源检索（新建批次第 2 步 / 详情页「添加工件」共用同一实现）。
// 后端契约（core-api task-10 / task-26）：
//   GET /api/rework-batches/customer-deliveries?customer_id=&q=&contract_id=&limit=&offset=
//   → { customer, contracts, assets, deliveries, total, limit, offset }
// q 跨「交货单号 / 合同号 / 序列号 / 工件编码」匹配；deliveries 按 total 分页；
// assets 为该客户工件表（按 q 过滤），用于「按工件定位」其最近交货行。
//
// task-26 起：每条 delivery line 带 pieces[]（逐件 serial_number / asset_id / service_cycle_id /
// in_active_rework / original_inspection）。同一交货行含多个序列号时按「件」逐行渲染、逐件勾选 ——
// 勾 1 件只提交 1 件，不再整行选择。旧响应没有 pieces 时退化为按整行 1 件（保留旧口径）。
// 「选择方式」支持按交货单 / 按销售合同：合同模式必须选合同，并把该合同下所有可返修工件平铺逐件勾选。
// 设计文档 docs/aftersales-rework-batch-redesign-2026-10-16.md v3。

export type SourceInspCandidate = {
  id: string;
  number: string;
  result?: string | null;
  inspection_date?: string | null;
  match_reason?: string | null;
  snapshot_available?: boolean;
};
export type SourceInspection = {
  match: 'unique' | 'multiple' | 'weak' | 'none';
  resolved_id?: string | null;
  message?: string | null;
  candidates?: SourceInspCandidate[];
};
export type SourcePiece = {
  /** 后端下发的逐件稳定标识（line.id + ':' + serial 或 line.id + ':0'）。 */
  piece_key?: string | null;
  serial_number?: string | null;
  asset_id?: string | null;
  service_cycle_id?: string | null;
  in_active_rework?: number;
  original_inspection?: SourceInspection;
};
export type SourceLine = {
  id: string;
  delivery_id: string;
  item_id?: string | null;
  description_es?: string | null;
  quantity?: number;
  uom?: string;
  asset_id?: string | null;
  service_cycle_id?: string | null;
  serial_numbers?: string | null;
  customer_serial?: string | null;
  internal_code?: string | null;
  item_code?: string | null;
  item_name_zh?: string | null;
  in_active_rework?: number;
  original_inspection?: SourceInspection;
  /** 逐件工件（task-26 起后端下发；旧响应可能缺失）。 */
  pieces?: SourcePiece[] | null;
};
export type SourceDelivery = {
  id: string;
  number: string;
  delivery_date?: string | null;
  status?: string | null;
  contract_id?: string | null;
  contract_number?: string | null;
  lines: SourceLine[];
};
export type SourceAsset = {
  asset_id: string;
  customer_serial?: string | null;
  internal_code?: string | null;
  item_id?: string | null;
  item_code?: string | null;
  item_name_zh?: string | null;
  // task-14 起后端下发「最近交货」锚点：可直接定位并精确勾选该交货行。
  latest_delivery_id?: string | null;
  latest_delivery_line_id?: string | null;
  latest_delivery_number?: string | null;
  latest_delivery_date?: string | null;
  latest_contract_number?: string | null;
  // 兼容 task-14 之前的响应字段（无交货行 id，只能按工件身份在该交货单内反查）。
  last_delivery_number?: string | null;
  last_delivery_date?: string | null;
};
export type SourceContract = { id: string; number: string; contract_date?: string | null; status?: string | null };
type SourceResponse = {
  customer?: unknown;
  contracts?: SourceContract[];
  assets?: SourceAsset[];
  deliveries?: SourceDelivery[];
  total?: number;
  limit?: number;
  offset?: number;
};

// 规范化后的一件工件：渲染/选中/提交统一用它，旧响应（无 pieces）退化为「整行一件」。
export type PieceSelection = {
  piece_key: string;
  serial_number: string | null;
  asset_id: string | null;
  service_cycle_id: string | null;
  in_active_rework: number;
  original_inspection: SourceInspection;
  /** true = 旧响应按交货行整行选择（数量可编辑、提交不带 serial_number）。 */
  legacy_line: boolean;
};

const EMPTY_INSPECTION: SourceInspection = { match: 'none', candidates: [] };

const PAGE = 50;

const norm = (s?: string | null) => String(s || '').trim().toLowerCase();
const serialKeys = (s?: string | null) => norm(s).split(/[,;|\s]+/).filter(Boolean);

// 交货行 ↔ 工件身份匹配：asset_id 强匹配；否则按序列号/内部编码（含交货行的 serial_numbers 多值）匹配。
function lineMatchesAsset(line: SourceLine, asset: SourceAsset): boolean {
  if (asset.asset_id && line.asset_id === asset.asset_id) return true;
  const wanted = [asset.customer_serial, asset.internal_code].map(norm).filter(Boolean);
  if (!wanted.length) return false;
  const have = [
    ...serialKeys(line.customer_serial),
    ...serialKeys(line.internal_code),
    ...serialKeys(line.serial_numbers),
  ];
  return wanted.some((w) => have.includes(w));
}

// 把一条交货明细展开为逐件选择项：后端 pieces[] 优先；旧响应无 pieces 时整行 1 件。
export function normalizeLinePieces(line: SourceLine): PieceSelection[] {
  const raw = Array.isArray(line.pieces) ? line.pieces : [];
  if (raw.length) {
    return raw.map((p, idx) => ({
      piece_key: p.piece_key || `${line.id}:${p.serial_number || idx}`,
      serial_number: p.serial_number || null,
      asset_id: p.asset_id || null,
      service_cycle_id: p.service_cycle_id || null,
      in_active_rework: Number(p.in_active_rework) || 0,
      original_inspection: p.original_inspection || line.original_inspection || EMPTY_INSPECTION,
      legacy_line: false,
    }));
  }
  return [{
    piece_key: line.id,
    serial_number: null,
    asset_id: line.asset_id || null,
    service_cycle_id: line.service_cycle_id || null,
    in_active_rework: Number(line.in_active_rework) || 0,
    original_inspection: line.original_inspection || EMPTY_INSPECTION,
    legacy_line: true,
  }];
}

// 单件展示名：逐件序列号优先，其次交货行上的客户序列号/内部编码/序列号文本。
export function pieceLabel(line: SourceLine, piece: PieceSelection): string {
  return piece.serial_number || line.customer_serial || line.internal_code || line.serial_numbers || line.description_es || '工件';
}

// 工件 → 最近交货行锚点。task-14 assets 提供 latest_delivery_line_id/number；
// 旧响应只有 last_delivery_number，则以工件身份（asset_id/序列号）在该交货内反查。
type AssetTarget = { lineId: string; deliveryNumber: string; asset: SourceAsset };

function latestDelivery(a: SourceAsset): { lineId: string; deliveryNumber: string; date: string; contractNumber: string } {
  return {
    lineId: a.latest_delivery_line_id || '',
    deliveryNumber: a.latest_delivery_number || a.last_delivery_number || '',
    date: a.latest_delivery_date || a.last_delivery_date || '',
    contractNumber: a.latest_contract_number || '',
  };
}

// 工件定位到「具体一件」：在最近交货行内按 asset_id / 序列号找到对应 piece；
// 该行绑定多个序列号且都不匹配时返回 null（提示用户按序列号检索后手选）。
function findAssetPiece(
  deliveries: SourceDelivery[],
  target: AssetTarget,
): { d: SourceDelivery; line: SourceLine; piece: PieceSelection } | null {
  const wantedSerials = new Set([target.asset.customer_serial, target.asset.internal_code].map(norm).filter(Boolean));
  for (const d of deliveries) {
    if (target.deliveryNumber && d.number !== target.deliveryNumber) continue;
    const line = target.lineId
      ? d.lines.find((l) => l.id === target.lineId)
      : d.lines.find((l) => lineMatchesAsset(l, target.asset));
    if (!line) continue;
    const pieces = normalizeLinePieces(line);
    const hit = pieces.find((p) => {
      if (target.asset.asset_id && p.asset_id === target.asset.asset_id) return true;
      return Boolean(p.serial_number && wantedSerials.has(norm(p.serial_number)));
    });
    if (hit) return { d, line, piece: hit };
    // 旧响应无 pieces（整行即一件），或单件行且行级 asset 与目标一致：按这一件回退。
    if (pieces.length === 1 && (pieces[0].legacy_line || line.asset_id === target.asset.asset_id)) {
      return { d, line, piece: pieces[0] };
    }
    return null;
  }
  return null;
}

function InspectionCell({ insp }: { insp: SourceInspection }) {
  if (insp.match === 'unique') {
    return <span>已关联 {insp.candidates?.[0]?.number || insp.resolved_id || '—'}</span>;
  }
  if (insp.match === 'multiple') {
    return <span style={{ color: 'var(--bad)' }}>多个候选（{insp.candidates?.length || 0}）· 需显式选择</span>;
  }
  if (insp.match === 'weak') {
    return <span style={{ color: 'var(--bad)' }}>仅弱候选（工件+合同）· 需显式确认对应交付</span>;
  }
  return <span style={{ color: 'var(--bad)' }}>未定位原检验 · 由质检确认模板</span>;
}

function PieceRow({ delivery, line, piece, selected, onToggle }: {
  delivery: SourceDelivery;
  line: SourceLine;
  piece: PieceSelection;
  selected: Set<string>;
  onToggle: (delivery: SourceDelivery, line: SourceLine, piece: PieceSelection, checked: boolean) => void;
}) {
  const on = selected.has(piece.piece_key);
  const label = pieceLabel(line, piece);
  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={on}
          onChange={() => onToggle(delivery, line, piece, !on)}
          aria-label={`选择工件 ${label}`}
        />
      </td>
      <td className="muted">
        {label}
        {piece.in_active_rework > 0
          ? <span title="该工件已在另一进行中的返修批次内，来源确认时会被拦截"> · 已在返修中</span>
          : null}
      </td>
      <td className="muted">{line.item_name_zh || line.item_code || '—'}</td>
      <td className="muted">{delivery.number}</td>
      <td className="muted">{delivery.contract_number || '—'}</td>
      <td className="muted"><InspectionCell insp={piece.original_inspection} /></td>
    </tr>
  );
}

export function SourceSearch({ customerId, selectedKeys, onTogglePiece, hint, emptyHint }: {
  customerId: string;
  /** 已选工件 piece_key（跨搜索/分页/合同筛选保持）。 */
  selectedKeys: string[];
  onTogglePiece: (delivery: SourceDelivery, line: SourceLine, piece: PieceSelection, checked: boolean) => void;
  hint?: string;
  emptyHint?: string;
}) {
  // 选择方式：按交货单（原口径）/ 按销售合同（合同必选后平铺逐件）。
  const [mode, setMode] = useState<'delivery' | 'contract'>('delivery');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [contractId, setContractId] = useState('');
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [contracts, setContracts] = useState<SourceContract[]>([]);
  const [assets, setAssets] = useState<SourceAsset[]>([]);
  const [deliveries, setDeliveries] = useState<SourceDelivery[]>([]);
  const [total, setTotal] = useState(0);
  const [paged, setPaged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [assetMsg, setAssetMsg] = useState('');
  // 最近一次「已完成」请求的搜索词：用于判断按工件定位触发的重查是否已返回（避免拿旧结果误报未找到）。
  const [lastLoadedQ, setLastLoadedQ] = useState<string | null>(null);
  // 按工件定位的待勾选目标：用最近交货单号重查后，在结果中按 line_id/asset 精确勾选对应序列号。
  const [pendingAsset, setPendingAsset] = useState<(AssetTarget & { label: string }) | null>(null);

  // 搜索防抖：输入 300ms 后再请求，避免每键一次查询。
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!customerId) { setDeliveries([]); setAssets([]); setContracts([]); setTotal(0); return; }
    let alive = true;
    const requestedQ = debouncedQ;
    setLoading(true);
    setErr('');
    setAssetMsg('');
    const params = new URLSearchParams({ customer_id: customerId, limit: String(PAGE), offset: String(offset) });
    if (requestedQ) params.set('q', requestedQ);
    if (contractId) params.set('contract_id', contractId);
    api.detail<SourceResponse>(`/api/rework-batches/customer-deliveries?${params.toString()}`)
      .then((data) => {
        if (!alive) return;
        // 后端在带 contract_id 时会按该合同收窄 contracts；合并保留首次的完整合同列表，
        // 避免选中一个合同后下拉只剩「该合同 + 全部」而无法直接切换其它合同。
        setContracts((prev) => {
          const next = data.contracts || [];
          if (!contractId) return next;
          const map = new Map(prev.map((c) => [c.id, c]));
          for (const c of next) map.set(c.id, c);
          return Array.from(map.values());
        });
        setAssets(data.assets || []);
        setDeliveries(data.deliveries || []);
        if (typeof data.total === 'number') {
          setTotal(data.total);
          setPaged(true);
        } else {
          // 旧响应没有 total：不知道还有没有下一页，关闭分页避免误导。
          setTotal((data.deliveries || []).length);
          setPaged(false);
        }
      })
      .catch((e: any) => {
        if (!alive) return;
        setErr(e?.message || '来源查询失败');
        setDeliveries([]);
        setAssets([]);
        setTotal(0);
      })
      .finally(() => { if (alive) { setLastLoadedQ(requestedQ); setLoading(false); } });
    return () => { alive = false; };
  }, [customerId, debouncedQ, contractId, offset, reloadKey]);

  // 用户改动搜索词/客户/合同/选择方式时取消未完成的定位，避免按钮一直停在「定位中…」。
  useEffect(() => {
    if (pendingAsset && q.trim() !== pendingAsset.deliveryNumber) setPendingAsset(null);
  }, [q, pendingAsset]);
  useEffect(() => { setPendingAsset(null); }, [customerId, contractId, mode]);
  // 换客户后旧合同筛选不属于新客户，清空避免把上一客户的合同条件带进新查询。
  useEffect(() => { setContractId(''); setQ(''); }, [customerId]);

  // 按工件定位：等带最近交货单号的这次查询返回后，再按 line_id/asset 精确勾选对应那一件。
  // 找不到时给可操作提示（刷新重试 / 改用序列号手动勾选），不再用「请缩小范围」把用户困住。
  useEffect(() => {
    if (!pendingAsset || loading) return;
    if (lastLoadedQ !== pendingAsset.deliveryNumber) return;
    const found = findAssetPiece(deliveries, pendingAsset);
    if (found) {
      // 定位语义是「选中」，已选中时重复点也不会取消。
      onTogglePiece(found.d, found.line, found.piece, true);
      setAssetMsg(`已勾选「${pendingAsset.label}」的最近交货工件（${found.d.number}）。`);
    } else {
      setAssetMsg(
        `未找到交货单「${pendingAsset.deliveryNumber}」中该工件对应的具体序列号（可能已被修改）。`
        + '请点上方「刷新」重试；如已选合同筛选，请切回「全部合同」；也可改用序列号搜索后手动勾选。',
      );
    }
    setPendingAsset(null);
  }, [pendingAsset, loading, lastLoadedQ, deliveries, onTogglePiece]);

  const selected = new Set(selectedKeys);
  const hasMore = offset + deliveries.length < total;

  // 合同模式：该合同下所有可返修工件平铺为逐件行（按交货单顺序）。
  const flatPieces = deliveries.flatMap((d) => d.lines.flatMap((line) => (
    normalizeLinePieces(line).map((piece) => ({ delivery: d, line, piece }))
  )));
  const contractNumber = contracts.find((c) => c.id === contractId)?.number || '';

  // 「按工件定位」→ 选中该工件最近一次交货行中对应的那一件：
  //  ① 当前页已含该行 → 直接勾选；
  //  ② 否则用最近交货单号重查，并在结果中按 latest_delivery_line_id + asset/序列号 精确定位；
  //  ③ 工件无历史交货 → 返回 false，由按钮显示「无历史交货」。
  const pickAsset = (asset: SourceAsset) => {
    setAssetMsg('');
    const info = latestDelivery(asset);
    const label = asset.customer_serial || asset.internal_code || asset.asset_id;
    if (!info.lineId && !info.deliveryNumber) {
      setAssetMsg(`工件「${label}」无历史交货记录，无法按交货行定位；请改用「手工登记工件」。`);
      return;
    }
    const target: AssetTarget = { lineId: info.lineId, deliveryNumber: info.deliveryNumber, asset };
    const direct = findAssetPiece(deliveries, target);
    if (direct) {
      onTogglePiece(direct.d, direct.line, direct.piece, true);
      setAssetMsg(`已勾选「${label}」的最近交货工件（${direct.d.number}）。`);
      return;
    }
    if (!info.deliveryNumber) {
      setAssetMsg(`工件「${label}」缺少最近交货单号，无法自动定位；请用序列号搜索后手动勾选。`);
      return;
    }
    if (lastLoadedQ === info.deliveryNumber) {
      setAssetMsg(
        `未找到交货单「${info.deliveryNumber}」中该工件对应的具体序列号（可能已被修改）。`
        + '请点上方「刷新」重试；如已选合同筛选，请切回「全部合同」；也可改用序列号搜索后手动勾选。',
      );
      return;
    }
    // 用最近交货单号作为搜索词重查（query 变化会自动防抖触发），返回后按 line_id + 序列号精确定位。
    setQ(info.deliveryNumber);
    setPendingAsset({ ...target, label });
  };

  // 交货单模式下的合同筛选 / 合同模式下的必选合同（同一状态，合同模式的文案与选项不同）。
  const contractField = (
    <div className="field" style={{ minWidth: 220 }}>
      <label>{mode === 'contract' ? '销售合同 *' : '合同'}</label>
      <select
        className="sel"
        value={contractId}
        onChange={(e) => { setContractId(e.target.value); setOffset(0); }}
        aria-label={mode === 'contract' ? '按销售合同选择' : '按合同筛选历史交货'}
      >
        {mode === 'contract'
          ? <option value="">请选择销售合同…</option>
          : <option value="">全部合同</option>}
        {contracts.map((c) => (
          <option key={c.id} value={c.id}>{c.number}{c.contract_date ? ` · ${c.contract_date}` : ''}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      <div className="toolbar" style={{ alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <div className="field">
          <label>选择方式</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              className={`btn ${mode === 'delivery' ? 'primary' : ''}`}
              onClick={() => { setMode('delivery'); setOffset(0); }}
            >按交货单选择</button>
            <button
              type="button"
              className={`btn ${mode === 'contract' ? 'primary' : ''}`}
              onClick={() => { setMode('contract'); setOffset(0); }}
            >按销售合同选择</button>
          </div>
        </div>
        {mode === 'contract' ? contractField : null}
        <div className="field" style={{ flex: 1, minWidth: 240 }}>
          <label>搜索历史来源</label>
          <SearchField value={q} onChange={setQ} placeholder="交货单号 / 合同号 / 序列号 / 工件编码" />
        </div>
        {mode === 'delivery' ? contractField : null}
        <div className="field">
          <label>操作</label>
          <button className="btn" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>刷新</button>
        </div>
      </div>
      {hint ? <div className="muted" style={{ fontSize: 12, margin: '4px 0' }}>{hint}</div> : null}
      {mode === 'contract' ? (
        <div className="muted" style={{ fontSize: 12, margin: '4px 0' }}>
          按销售合同选择：先选择销售合同，再把该合同下所有可返修工件按件平铺；逐件勾选后与已选摘要联动（跨模式保持）。
        </div>
      ) : null}
      {err ? <div className="notice bad">{err}</div> : null}

      {assets.length > 0 ? (
        <div className="panel" style={{ margin: '8px 0', padding: 12 }}>
          <div className="strong">按工件定位（命中 {assets.length} 件）</div>
          <div className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
            按工件序列号/内部编码定位，可直接选中其最近一次交货中的具体那一件加入本次工件。
          </div>
          <table className="grid">
            <thead><tr><th>工件 / 内部编码</th><th>物料</th><th>最近交货</th><th>合同</th><th></th></tr></thead>
            <tbody>
              {assets.map((a) => {
                const d = latestDelivery(a);
                const hasHistory = Boolean(d.lineId || d.deliveryNumber);
                const locating = pendingAsset?.asset.asset_id === a.asset_id;
                return (
                  <tr key={a.asset_id}>
                    <td className="muted">{a.customer_serial || a.internal_code || a.asset_id}</td>
                    <td className="muted">{a.item_name_zh || a.item_code || '—'}</td>
                    <td className="muted">
                      {d.deliveryNumber
                        ? `${d.deliveryNumber}${d.date ? ` · ${d.date}` : ''}`
                        : '无历史交货'}
                    </td>
                    <td className="muted">{d.contractNumber || '—'}</td>
                    <td>
                      {hasHistory ? (
                        <button className="btn" onClick={() => pickAsset(a)} disabled={loading || locating}>
                          {locating ? '定位中…' : '选最近交货工件'}
                        </button>
                      ) : (
                        <span
                          className="muted"
                          style={{ fontSize: 12 }}
                          title="该工件没有历史交货单，无法按交货行定位；可切到「手工登记工件」"
                        >无历史交货</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {assetMsg ? <div className="notice">{assetMsg}</div> : null}
        </div>
      ) : null}

      {loading ? <Spinner label="正在读取该客户历史交货…" /> : null}

      {mode === 'delivery' ? (
        <>
          {!loading && !err && deliveries.length === 0 ? (
            <div className="notice">
              {emptyHint || (debouncedQ || contractId
                ? '没有匹配的历史交货。请更换关键字或合同后重试。'
                : '该客户暂无历史交货单，可直接保存草稿，稍后在详情页追加或手工登记工件。')}
            </div>
          ) : null}

          {!loading ? deliveries.map((d) => (
            <div key={d.id} className="panel" style={{ margin: '8px 0', padding: 12 }}>
              <div className="strong">
                交货单 {d.number}
                {d.contract_number ? <span className="muted"> · 合同 {d.contract_number}</span> : null}
                {d.delivery_date ? <span className="muted"> · {d.delivery_date}</span> : null}
                <span className="muted"> · 明细 {d.lines.length} 行 / 工件 {d.lines.reduce((n, l) => n + normalizeLinePieces(l).length, 0)} 件</span>
              </div>
              <table className="grid" style={{ marginTop: 6 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th><th>工件 / 序列号</th><th>物料</th>
                    <th>原交货单</th><th>原合同</th><th>原检验</th>
                  </tr>
                </thead>
                <tbody>
                  {d.lines.flatMap((line) => normalizeLinePieces(line).map((piece) => (
                    <PieceRow
                      key={`${d.id}:${piece.piece_key}`}
                      delivery={d}
                      line={line}
                      piece={piece}
                      selected={selected}
                      onToggle={onTogglePiece}
                    />
                  )))}
                </tbody>
              </table>
            </div>
          )) : null}
        </>
      ) : (
        <>
          {!contractId && !loading ? (
            <div className="notice">按销售合同选择：请先在上方选择一张销售合同，再逐件勾选该合同下的可返修工件。</div>
          ) : null}
          {contractId && !loading && !err && deliveries.length === 0 ? (
            <div className="notice">销售合同 {contractNumber || contractId} 下暂无可返修工件（可能尚未交货，或交货单已取消）。</div>
          ) : null}
          {contractId && !loading && deliveries.length > 0 ? (
            <div className="panel" style={{ margin: '8px 0', padding: 12 }}>
              <div className="strong">
                销售合同 {contractNumber || contractId}
                <span className="muted"> · 可返修工件 {flatPieces.length} 件 / 交货单 {deliveries.length} 张</span>
              </div>
              <div className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
                该合同下所有可返修工件已按件平铺，勾选哪件就只提交哪件；分页按交货单翻页，已选跨页保持。
              </div>
              {flatPieces.length === 0 ? (
                <div className="notice">该合同下暂无可返修工件。</div>
              ) : (
                <table className="grid">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th><th>工件 / 序列号</th><th>物料</th>
                      <th>原交货单</th><th>原合同</th><th>原检验</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flatPieces.map(({ delivery, line, piece }) => (
                      <PieceRow
                        key={`${delivery.id}:${piece.piece_key}`}
                        delivery={delivery}
                        line={line}
                        piece={piece}
                        selected={selected}
                        onToggle={onTogglePiece}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </>
      )}

      {paged ? (
        <div className="pager">
          <span className="info">
            {total === 0
              ? '无匹配记录'
              : mode === 'contract'
                ? `第 ${offset + 1}–${offset + deliveries.length} 张交货单 / 共 ${total} 张`
                : `第 ${offset + 1}–${offset + deliveries.length} 条 / 共 ${total} 条`}
          </span>
          <div className="spacer" />
          <button className="btn" onClick={() => setOffset((o) => Math.max(0, o - PAGE))} disabled={offset === 0 || loading}>上一页</button>
          <button className="btn" onClick={() => setOffset((o) => o + PAGE)} disabled={!hasMore || loading}>下一页</button>
        </div>
      ) : null}
    </div>
  );
}
